import { ApiClient, type ApiClientOptions } from '@/infra/api-client'
import type {
  AuthUserResponse,
  EnrollMFAResponse,
  UserDataResource,
  UserResource,
  WorkspaceMembershipResource,
} from '@/features/iam/api/resources/users'
import type { WorkspaceResource } from '@/features/iam/api/resources/workspaces'
import type {
  CreateMemberRequest,
  GetManyMembersRequest,
  MemberResource,
  UpdateMemberRequest,
} from '@/features/iam/api/resources/members'
import type {
  ClaimMagicLinkSessionRequest,
  ClaimMagicLinkSessionResponse,
  RefreshMagicLinkTokenRequest,
  RefreshMagicLinkTokenResponse,
  RequestMagicLinkRequest,
  GenerateSelfMagicLinkRequest,
  GenerateSelfMagicLinkResponse,
} from '@/features/iam/api/resources/magic-link'
import type { TranslationResource } from '@/features/iam/api/resources/translations'

/**
 * Collection of APIs for IAM.
 */
export class IamApi {
  readonly authentication
  readonly users
  readonly workspaces
  readonly members
  readonly userData
  readonly translations
  readonly magicLinksV4

  constructor(options: ApiClientOptions) {
    this.authentication = new AuthenticateApi(options)
    this.users = new UserApi(options)
    this.workspaces = new WorkspaceApi(options)
    this.members = new MemberApi(options)
    this.userData = new UserDataApi(options)
    this.translations = new TranslationApi(options)
    this.magicLinksV4 = new MagicLinkV4Api(options)
  }
}

export class AuthenticateApi extends ApiClient {
  authenticate(token: string, signal?: AbortSignal): Promise<AuthUserResponse> {
    return this._post('v3/auth', {}, undefined, {
      signal,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
  }

  enrollMFA(): Promise<EnrollMFAResponse> {
    return this._post('v3/auth/enroll-mfa', {})
  }
}

export class UserApi extends ApiClient {
  get(
    userId: string,
    bearerToken?: string,
    signal?: AbortSignal,
  ): Promise<UserResource> {
    const init: RequestInit | undefined = bearerToken
      ? { headers: { Authorization: `Bearer ${bearerToken}` } }
      : undefined
    return this._get(`v3.1/users/${encodeURIComponent(userId)}`, undefined, {
      ...init,
      signal,
    })
  }

  getMany(userIds: readonly string[]): Promise<(UserResource | null)[]> {
    return this._post(`v3.1/users/id-query`, userIds)
  }

  getUserWorkspaceMemberships(
    userId: string,
    signal?: AbortSignal,
  ): Promise<WorkspaceMembershipResource[]> {
    return this._get(
      `v3.1/users/${encodeURIComponent(userId)}/workspace-memberships`,
      undefined,
      {
        signal,
      },
    )
  }

  async requestPasswordReset(
    workspaceId: string,
    email: string,
  ): Promise<void> {
    const request = new Request(
      new URL('v3/users/reset-password-request', this.options.baseURL),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ workspace_id: workspaceId, email }),
      },
    )

    await this._send(request).then(ApiClient.throwIfNotSuccessful)
  }
}

export class UserDataApi extends ApiClient {
  get(userId: string, signal?: AbortSignal): Promise<UserDataResource> {
    return this._get(`v3/users/${userId}/user-data`, undefined, { signal })
  }
}

export class WorkspaceApi extends ApiClient {
  getMany(
    workspaceIds: readonly string[],
  ): Promise<(WorkspaceResource | null)[]> {
    return this._post(
      `v3/workspaces/id-query`,
      workspaceIds.map((id) => ({ id })),
    )
  }
}

export class MemberApi extends ApiClient {
  getMany(
    request: GetManyMembersRequest,
    signal?: AbortSignal,
  ): Promise<[MemberResource | null]> {
    const ids = request.ids.map((id) => ({
      workspace_id: id.workspaceId,
      user_public_id: id.userId,
    }))
    return this._post(`v3/members/id-query`, ids, undefined, { signal })
  }

  create(request: CreateMemberRequest): Promise<MemberResource> {
    return this._post('v3/members', request)
  }

  update(
    workspaceId: string,
    userId: string,
    request: UpdateMemberRequest,
  ): Promise<MemberResource> {
    return this._patch(
      `v3.1/workspaces/${workspaceId}/members/${userId}`,
      request,
    )
  }

  async updateLastLogin(workspaceId: string): Promise<void> {
    const request = new Request(
      new URL(`v3/workspaces/${workspaceId}/login`, this.options.baseURL),
      {
        priority: 'low',
        method: 'PATCH',
      },
    )
    await this._send(request).then(ApiClient.throwIfNotSuccessful)
  }

  async updateLastSeen(workspaceId: string): Promise<void> {
    const request = new Request(
      new URL(`v3/workspaces/${workspaceId}/ping`, this.options.baseURL),
      {
        priority: 'low',
        method: 'PATCH',
      },
    )
    await this._send(request).then(ApiClient.throwIfNotSuccessful)
  }

  changeAvatar(
    workspaceId: string,
    userId: string,
    file: File,
    signal?: AbortSignal,
  ): Promise<MemberResource> {
    const form = new FormData()
    form.append('file', file, file.name)
    return this._put(
      `v3.1/workspaces/${workspaceId}/members/${userId}/avatar`,
      undefined,
      undefined,
      {
        body: form,
        headers: {},
        signal,
      },
    )
  }
}

export class MagicLinkV4Api extends ApiClient {
  claimSession(
    request: ClaimMagicLinkSessionRequest,
    signal?: AbortSignal,
  ): Promise<ClaimMagicLinkSessionResponse> {
    return this._post(
      'v4/magic-link/claim-session',
      request,
      undefined,
      { signal },
      { unauthenticated: true },
    )
  }

  refreshToken(
    request: RefreshMagicLinkTokenRequest,
    signal?: AbortSignal,
  ): Promise<RefreshMagicLinkTokenResponse> {
    return this._post(
      'v4/magic-link/refresh-token',
      request,
      undefined,
      { signal },
      { unauthenticated: true },
    )
  }

  requestMagicLink(
    request: RequestMagicLinkRequest,
    signal?: AbortSignal,
  ): Promise<void> {
    return this._post(
      'v4/magic-link/request',
      request,
      undefined,
      { signal },
      { unauthenticated: true },
    )
  }

  generateSelfMagicLink(
    request: GenerateSelfMagicLinkRequest,
    signal?: AbortSignal,
  ): Promise<GenerateSelfMagicLinkResponse> {
    return this._post('v4/magic-link/self', request, undefined, { signal })
  }
}

export class TranslationApi extends ApiClient {
  getAll(
    workspaceId: string,
    signal?: AbortSignal,
  ): Promise<TranslationResource[]> {
    return this._get(`v3/translations/${workspaceId}`, undefined, { signal })
  }
}
